import './ClientPhotoStudioPinturaOverrides.sass'

import {
  createDefaultColorOptions,
  createDefaultImageScrambler,
  getEditorDefaults
} from '@pqina/pintura'
import { ReactElement, useEffect, useRef, useState } from 'react'

import { AnalyticsEvent } from 'utils/analytics'
import { ManualToolOriginalImagePreview } from './ManualToolOriginalImagePreview.component'
import { PinturaEditor } from '@pqina/react-pintura'
import { Stack } from '@mui/material'
import { UndoRedoPanel } from '../../common/UndoRedoPanel'
import { VisualTypeSwitcher } from '../../common/VisualTypeSwitcher'
import { createRoot } from 'react-dom/client'
import { customizeToolbarButtons } from '../PhotoStudioUtils'
import { getPinturaLocale } from 'utils/helpers'
import i18n from 'translations/i18n'
import { logger } from 'utils/logging'
import { useClientPhotoStudio } from './ClientPhotoStudio.context'
import { useLabViewSwitcherProps } from './lab/useLabViewSwitcherProps.hook'
import { useTranslation } from 'react-i18next'
import { useWorkspacePlan } from 'utils/hooks'

const PINTURA_EDITOR_DEFAULTS = getEditorDefaults({
  stickers: ['😎', '🎉', '😄', '👍', '👎'],
})

/**
 * Client Photo Studio Manual tools preview (Pintura features for now).
 * @example <ClientPhotoStudioManualToolsPreview/>
 */
export const ClientPhotoStudioManualToolsPreview: React.FC = () => {
  const { t } = useTranslation(['photo_studio_editing'])

  const { hasWorkspaceActiveSubscription } = useWorkspacePlan()

  const {
    previewImage,
    editorRef,
    handleUpdate,
    selectedPinturaFeature,
    logPhotoStudioEvent,
    setPinturaHistory,
    expandedVisualType,
    setExpandedVisualType,
    isOriginalVisualType,
    imageUrl,
    photoStudioHistory,
    isLabSdkEngine,
    isLabCompareAvailable,
    isLabCompare,
  } = useClientPhotoStudio()
  const labSwitcherProps = useLabViewSwitcherProps()

  // What the roots injected into Pintura's toolbar render. Lab SDK engine: the same Before | Compare | After and
  // undo/redo as the other tools, both once there is something to undo or redo, the switch disabled while there is no
  // earlier version. Those roots live outside the Photo Studio context, hence explicit props, read by the effects
  // through refs.
  const { canUndo, canRedo } = photoStudioHistory.historyState
  const switcherContentRef = useRef<ReactElement | null>(null)
  switcherContentRef.current = !isLabSdkEngine
    ? <VisualTypeSwitcher expandedVisualType={expandedVisualType} switchExpandedVisualType={setExpandedVisualType} />
    : canUndo || canRedo ? <VisualTypeSwitcher {...labSwitcherProps} disabled={!isLabCompareAvailable} /> : null
  const undoRedoContentRef = useRef<ReactElement | null>(null)
  undoRedoContentRef.current = isLabSdkEngine && !canUndo && !canRedo
    ? null
    : (
      <UndoRedoPanel
        logPhotoStudioEvent={logPhotoStudioEvent}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={photoStudioHistory.undo}
        onRedo={photoStudioHistory.redo}
        disabled={isOriginalVisualType}
        compact={true}
        currentHistoryEntry={photoStudioHistory.historyState.current}
      />
    )
  // Lab SDK engine: Before and Compare are drawn by the preview below, over the hidden editor
  const isShowingOtherView = isOriginalVisualType || (isLabSdkEngine && isLabCompare)

  const rootRef = useRef<ReturnType<typeof createRoot> | null>(null)
  const elementRef = useRef<HTMLElement | null>(null)
  const undoRedoRootRef = useRef<ReturnType<typeof createRoot> | null>(null)
  const undoRedoElementRef = useRef<HTMLElement | null>(null)
  const [isPinturaLoaded, setIsPinturaLoaded] = useState(false)

  const isRootValid = () => {
    if (!rootRef.current || !elementRef.current) {
      return false
    }

    return document.contains(elementRef.current)
  }

  // Apply margin overrides directly via JavaScript as a fallback for dev/prod builds
  // This ensures the styles work even if CSS modules don't load correctly
  const applyMarginOverrides = () => {
    const navGroups = document.querySelectorAll('.PinturaRoot .PinturaNavGroup:not(.PinturaNavGroupFloat)')
    navGroups.forEach((group) => {
      const element = group as HTMLElement
      // Force override margins to 0 to prevent auto margins from pushing buttons to edges
      element.style.setProperty('margin-left', '0', 'important')
      element.style.setProperty('margin-right', '0', 'important')
    })
  }

  // Set up MutationObserver to watch for style changes and reapply our overrides
  useEffect(() => {
    if (!isPinturaLoaded) return

    // Apply overrides immediately
    applyMarginOverrides()

    const observer = new MutationObserver((mutations) => {
      // Reapply overrides whenever the DOM changes
      applyMarginOverrides()
    })

    // Observe the PinturaNav for changes (where toolbar buttons are)
    const pinturaNav = document.querySelector('.PinturaNav')
    if (pinturaNav) {
      observer.observe(pinturaNav, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      })
    }

    // Also observe the visual switcher element directly
    const visualSwitcherEl = document.querySelector('.pintura-visual-switcher') as HTMLElement
    if (visualSwitcherEl) {
      observer.observe(visualSwitcherEl, {
        attributes: true,
        attributeFilter: ['class', 'style'],
        subtree: true
      })
    }

    // Also observe the root for any PinturaNavGroup changes
    const pinturaRoot = document.querySelector('.PinturaRoot')
    if (pinturaRoot) {
      observer.observe(pinturaRoot, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['class', 'style']
      })
    }

    // Set up interval to periodically reapply (fallback for cases where observer doesn't catch changes)
    // Use a reasonable interval to avoid performance issues
    const intervalId = setInterval(() => {
      applyMarginOverrides()
    }, 500)

    return () => {
      observer.disconnect()
      clearInterval(intervalId)
    }
  }, [isPinturaLoaded])

  useEffect(() => {
    if (!isPinturaLoaded) return

    const findAndRenderSwitcher = () => {
      const el = document.querySelector('.pintura-visual-switcher') as HTMLElement

      if (!el) return false

      const elementChanged = elementRef.current !== el || !isRootValid()

      if (elementChanged) {
        // If root exists but element changed, unmount previous root
        if (rootRef.current && elementRef.current) {
          try {
            rootRef.current.unmount()
          } catch (e) {
            logger.warn('Error unmounting previous root', e, {
              context: {
                component: 'ClientPhotoStudioManualToolsPreview',
                action: 'unmountPreviousRoot'
              }
            })
          }
        }

        rootRef.current = createRoot(el)
        elementRef.current = el
      } else if (!rootRef.current) {
        // If rootRef is null, create a new root
        rootRef.current = createRoot(el)
        elementRef.current = el
      }

      rootRef.current.render(switcherContentRef.current)

      return true
    }

    let retryId: ReturnType<typeof setInterval> | null = null

    const timeoutId = setTimeout(() => {
      if (!findAndRenderSwitcher()) {
        let attempts = 0
        const maxAttempts = 10
        const retryDelay = 100

        retryId = setInterval(() => {
          attempts++
          if (findAndRenderSwitcher() || attempts >= maxAttempts) {
            if (retryId) {
              clearInterval(retryId)
              retryId = null
            }
          }
        }, retryDelay)
      }
    }, 0)

    return () => {
      clearTimeout(timeoutId)
      if (retryId) {
        clearInterval(retryId)
      }
    }
  }, [isPinturaLoaded, expandedVisualType, setExpandedVisualType, isLabSdkEngine, isLabCompareAvailable, isLabCompare, canUndo, canRedo])

  // Inject undo/redo panel into Pintura toolbar
  useEffect(() => {
    if (!isPinturaLoaded) return

    const findAndRenderUndoRedo = () => {
      const el = document.querySelector('.pintura-undo-redo') as HTMLElement

      if (!el) return false

      const elementChanged = undoRedoElementRef.current !== el || (undoRedoElementRef.current && !document.contains(undoRedoElementRef.current))

      if (elementChanged) {
        // If root exists but element changed, unmount previous root
        if (undoRedoRootRef.current && undoRedoElementRef.current) {
          try {
            undoRedoRootRef.current.unmount()
          } catch (e) {
            logger.warn('Error unmounting previous undo/redo root', e, {
              context: {
                component: 'ClientPhotoStudioManualToolsPreview',
                action: 'unmountPreviousUndoRedoRoot'
              }
            })
          }
        }

        undoRedoRootRef.current = createRoot(el)
        undoRedoElementRef.current = el
      } else if (!undoRedoRootRef.current) {
        // If rootRef is null, create a new root
        undoRedoRootRef.current = createRoot(el)
        undoRedoElementRef.current = el
      }

      undoRedoRootRef.current.render(undoRedoContentRef.current)

      return true
    }

    let retryId: ReturnType<typeof setInterval> | null = null

    const timeoutId = setTimeout(() => {
      if (!findAndRenderUndoRedo()) {
        let attempts = 0
        const maxAttempts = 10
        const retryDelay = 100

        retryId = setInterval(() => {
          attempts++
          if (findAndRenderUndoRedo() || attempts >= maxAttempts) {
            if (retryId) {
              clearInterval(retryId)
              retryId = null
            }
          }
        }, retryDelay)
      }
    }, 0)

    return () => {
      clearTimeout(timeoutId)
      if (retryId) {
        clearInterval(retryId)
      }
    }
  }, [isPinturaLoaded, photoStudioHistory, logPhotoStudioEvent, isOriginalVisualType, isLabSdkEngine])

  // When history state changes, update existing undo/redo panel
  useEffect(() => {
    const currentEl = document.querySelector('.pintura-undo-redo') as HTMLElement

    if (undoRedoRootRef.current && currentEl) {
      if (undoRedoElementRef.current !== currentEl) {
        if (undoRedoElementRef.current) {
          try {
            undoRedoRootRef.current.unmount()
          } catch (e) {
            logger.warn('Error unmounting previous undo/redo root', e, {
              context: {
                component: 'ClientPhotoStudioManualToolsPreview',
                action: 'unmountPreviousUndoRedoRootOnHistoryChange'
              }
            })
          }

          undoRedoRootRef.current = createRoot(currentEl)
          undoRedoElementRef.current = currentEl
        }
      }

      undoRedoRootRef.current.render(undoRedoContentRef.current)
    }
  }, [photoStudioHistory.historyState.canUndo, photoStudioHistory.historyState.canRedo, isPinturaLoaded, logPhotoStudioEvent, isOriginalVisualType, photoStudioHistory, isLabSdkEngine])

  // When expandedVisualType changes, update existing switcher
  useEffect(() => {
    const currentEl = document.querySelector('.pintura-visual-switcher') as HTMLElement

    if (rootRef.current && currentEl) {
      if (elementRef.current !== currentEl) {
        if (elementRef.current) {
          try {
            rootRef.current.unmount()
          } catch (e) {
            logger.warn('Error unmounting previous root', e, {
              context: {
                component: 'ClientPhotoStudioManualToolsPreview',
                action: 'unmountPreviousRootOnElementChange'
              }
            })
          }

          rootRef.current = createRoot(currentEl)
          elementRef.current = currentEl
        }
      }

      rootRef.current.render(switcherContentRef.current)
    }
  }, [expandedVisualType, isPinturaLoaded, setExpandedVisualType, isLabSdkEngine, isLabCompareAvailable, isLabCompare, canUndo, canRedo])

  // When history state changes, re-render visual switcher (Pintura may re-render toolbar)
  useEffect(() => {
    if (!isPinturaLoaded) return

    const findAndRenderSwitcher = () => {
      const currentEl = document.querySelector('.pintura-visual-switcher') as HTMLElement

      if (!currentEl) return false

      const elementChanged = elementRef.current !== currentEl || (elementRef.current && !document.contains(elementRef.current))

      if (elementChanged) {
        if (rootRef.current && elementRef.current) {
          try {
            rootRef.current.unmount()
          } catch (e) {
            logger.warn('Error unmounting previous root', e, {
              context: {
                component: 'ClientPhotoStudioManualToolsPreview',
                action: 'unmountPreviousRootOnHistoryChange'
              }
            })
          }
        }

        rootRef.current = createRoot(currentEl)
        elementRef.current = currentEl
      } else if (!rootRef.current) {
        rootRef.current = createRoot(currentEl)
        elementRef.current = currentEl
      }

      rootRef.current.render(switcherContentRef.current)

      return true
    }

    // Use a small delay to allow Pintura to finish re-rendering the toolbar
    let retryId: ReturnType<typeof setInterval> | null = null

    const timeoutId = setTimeout(() => {
      if (!findAndRenderSwitcher()) {
        // Retry a few times if element not found
        let attempts = 0
        const maxAttempts = 5
        const retryDelay = 100

        retryId = setInterval(() => {
          attempts++
          if (findAndRenderSwitcher() || attempts >= maxAttempts) {
            if (retryId) {
              clearInterval(retryId)
              retryId = null
            }
          }
        }, retryDelay)
      }
    }, 50)

    return () => {
      clearTimeout(timeoutId)
      if (retryId) {
        clearInterval(retryId)
        retryId = null
      }
    }
  }, [photoStudioHistory.historyState.canUndo, photoStudioHistory.historyState.canRedo, isPinturaLoaded, expandedVisualType, setExpandedVisualType, isLabSdkEngine, isLabCompareAvailable, isLabCompare])

  useEffect(() => {
    // Capture refs at effect setup time for cleanup
    const rootRefCurrent = rootRef
    const elementRefCurrent = elementRef
    const undoRedoRootRefCurrent = undoRedoRootRef
    const undoRedoElementRefCurrent = undoRedoElementRef

    return () => {
      // Defer unmount to next tick to avoid React race condition warning:
      // "Attempted to synchronously unmount a root while React was already rendering"
      setTimeout(() => {
        if (rootRefCurrent.current) {
          try {
            rootRefCurrent.current.unmount()
          } catch (e) {
            logger.warn('Error unmounting root on cleanup', e, {
              context: {
                component: 'ClientPhotoStudioManualToolsPreview',
                action: 'unmountRootOnCleanup'
              }
            })
          }
          rootRefCurrent.current = null
          elementRefCurrent.current = null
        }
        if (undoRedoRootRefCurrent.current) {
          try {
            undoRedoRootRefCurrent.current.unmount()
          } catch (e) {
            logger.warn('Error unmounting undo/redo root on cleanup', e, {
              context: {
                component: 'ClientPhotoStudioManualToolsPreview',
                action: 'unmountUndoRedoRootOnCleanup'
              }
            })
          }
          undoRedoRootRefCurrent.current = null
          undoRedoElementRefCurrent.current = null
        }
      }, 0)
    }
  }, [])

  const handlePinturaOnLoad = () => {
    setTimeout(() => {
      const el = document.querySelector('.pintura-visual-switcher') as HTMLElement

      if (el) {
        const elementChanged = elementRef.current !== el || (elementRef.current && !document.contains(elementRef.current))

        if (elementChanged) {
          if (rootRef.current && elementRef.current) {
            try {
              rootRef.current.unmount()
            } catch (e) {
              logger.warn('Error unmounting previous root', e, {
                context: {
                  component: 'ClientPhotoStudioManualToolsPreview',
                  action: 'unmountPreviousRootOnLoad'
                }
              })
            }
          }

          rootRef.current = createRoot(el)
          elementRef.current = el
        } else if (!rootRef.current) {
          // If rootRef is null, create a new root
          rootRef.current = createRoot(el)
          elementRef.current = el
        }

        if (rootRef.current) {
          rootRef.current.render(switcherContentRef.current)
        }
      }

      // Also inject undo/redo panel
      const undoRedoEl = document.querySelector('.pintura-undo-redo') as HTMLElement
      if (undoRedoEl) {
        const undoRedoElementChanged = undoRedoElementRef.current !== undoRedoEl || (undoRedoElementRef.current && !document.contains(undoRedoElementRef.current))

        if (undoRedoElementChanged) {
          if (undoRedoRootRef.current && undoRedoElementRef.current) {
            try {
              undoRedoRootRef.current.unmount()
            } catch (e) {
              logger.warn('Error unmounting previous undo/redo root', e, {
                context: {
                  component: 'ClientPhotoStudioManualToolsPreview',
                  action: 'unmountPreviousUndoRedoRootOnLoad'
                }
              })
            }
          }

          undoRedoRootRef.current = createRoot(undoRedoEl)
          undoRedoElementRef.current = undoRedoEl
        } else if (!undoRedoRootRef.current) {
          undoRedoRootRef.current = createRoot(undoRedoEl)
          undoRedoElementRef.current = undoRedoEl
        }

        if (undoRedoRootRef.current) {
          undoRedoRootRef.current.render(undoRedoContentRef.current)
        }
      }

      if (!isPinturaLoaded) {
        setIsPinturaLoaded(true)
      }

    }, 0)
  }

  useEffect(() => {
    if (!editorRef.current?.editor || !isPinturaLoaded) return

    // Lab SDK engine: Before is drawn by ManualToolOriginalImagePreview while the editor is hidden — leave it as is
    if (isOriginalVisualType && !isLabSdkEngine) {
      editorRef.current.editor.updateImagePreview(imageUrl)
    } else {
      editorRef.current.editor.updateImagePreview(previewImage)
    }
  }, [editorRef, imageUrl, isOriginalVisualType, isLabSdkEngine, isPinturaLoaded, previewImage])

  return (
    <>
      {(isShowingOtherView || !hasWorkspaceActiveSubscription) &&
        <ManualToolOriginalImagePreview />
      }

      {hasWorkspaceActiveSubscription && <div style={{
        height: '100%',
        position: 'relative',
      }}>
        <Stack display={isShowingOtherView ? 'none' : 'block'} height="100%">
          <PinturaEditor
            ref={editorRef}
            {...PINTURA_EDITOR_DEFAULTS}
            src={previewImage}
            onWritehistory={() => setPinturaHistory(editorRef.current?.editor.history.get())}
            onUndo={() => {
              setPinturaHistory(editorRef.current?.editor.history.get())
              logPhotoStudioEvent(AnalyticsEvent.PINTURA_PHOTO_STUDIO_UNDO)
            }}
            onRedo={() => {
              setPinturaHistory(editorRef.current?.editor.history.get())
              logPhotoStudioEvent(AnalyticsEvent.PINTURA_PHOTO_STUDIO_REDO)
            }}
            enableZoom={false}
            willRenderToolbar={customizeToolbarButtons}
            util={selectedPinturaFeature}
            enableUtils={false} // Disable utils to prevent showing the default toolbar, we render our own toolbar
            enableButtonRevert={false}
            enableButtonExport={false}
            animations='never'
            // @ts-ignore
            imageScrambler={createDefaultImageScrambler({
              scrambleAmount: 8,
              blurAmount: 20,
              dataSizeScalar: 8
            })}
            onUpdate={handleUpdate}
            onLoad={handlePinturaOnLoad}
            cropSelectPresetFilter={'landscape'}
            cropSelectPresetOptions={[
              [undefined, 'Custom'],
              [1, 'Square'],
              // shown when cropSelectPresetFilter is set to 'landscape'
              [2 / 1, '2:1'],
              [3 / 2, '3:2'],
              [4 / 3, '4:3'],
              [5 / 4, '5:4'],
              [16 / 10, '16:10'],
              [16 / 9, '16:9'],
              // shown when cropSelectPresetFilter is set to 'portrait'
              [1 / 2, '1:2'],
              [2 / 3, '2:3'],
              [3 / 4, '3:4'],
              [4 / 5, '4:5'],
              [10 / 16, '10:16'],
              [9 / 16, '9:16'],
            ]}
            fillOptions={[
              // Using the default markup editor colors
              ...Object.values(createDefaultColorOptions()),
            ]}
            locale={getPinturaLocale(i18n.language)}
            enableZoomControls={false}
            enableNavigateHistory={false}
          />
        </Stack>
      </div>}
    </>
  )
}
